// import shop from '../../api/shop'
import { ApiFactory } from "@/api/ApiFactory.js";
const OrdersApi = ApiFactory.get("orders");

const navigationHistoryLimit = 10;

function getNavigationHistoryFromLocalStorage() {
  let items = [];
  try {
    items = JSON.parse(localStorage.getItem("navigation-history"));
  } catch (e) {
    items = [];
  }

  if (!Array.isArray(items)) {
    return [];
  }

  return items;
}

// initial state
const state = () => ({
  orders: [],
  ordersLoaded: false,
  ordersSearching: false,
  order: {tracking: null},
  deprecatedOrder: {},
  deprecatedOrderLoaded: false,
  orderLoaded: false,
  orderSearching: false,
  navigationHistory: getNavigationHistoryFromLocalStorage(),
});

// getters
const getters = {
  getOrders(state) {
    return state.orders;
  },
  isOrdersLoaded(state) {
    return state.ordersLoaded;
  },
  isOrdersSearching(state) {
    return state.ordersSearching;
  },

  getOrder(state) {
    return state.order;
  },
  getDeprecatedOrder(state) {
    return state.deprecatedOrder;
  },
  isOrderLoaded(state) {
    return state.orderLoaded;
  },
  isDeprecatedOrderLoaded(state) {
    return state.deprecatedOrderLoaded;
  },
  isOrderSearching(state) {
    return state.orderSearching;
  },

  getNavigationHistory(state) {
    return state.navigationHistory.sort(
      (a, b) => b.lastAccessedAt - a.lastAccessedAt
    );
  },

  get: (state) => (key) => {
    return state[key];
  },
};

// actions
const actions = {
  fetchOrders({ commit, state }, filters) {
    // clear the current store if making a new search
    if (filters.newSearch) commit("SET_ORDERS", []);

    if (state.orders.length == 0) {
      commit("SET_ORDERS_LOADED", false);
      commit("SET_ORDERS_SEARCHING", true);
      return OrdersApi.get(filters).then((response) => {
        commit("SET_ORDERS", response.data.body);
        commit("SET_ORDERS_LOADED", true);
        commit("SET_ORDERS_SEARCHING", false);
      });
    }
  },

  async fetchOrder({ commit, dispatch }, OrderNumber) {
    commit("SET_ORDER", {});
    commit("SET_DEPRECATED_ORDER", {});
    commit("SET_ORDER_LOADED", false);
    commit("SET_ORDER_SEARCHING", true);
    commit("SET_DEPRECATED_ORDER_LOADED", false);
    let error = null;
    let response = await OrdersApi.getOrder(OrderNumber)
      .then((response) => {
        commit("SET_ORDER", response.data.body);
        commit("SET_ORDER_LOADED", true);
        dispatch("pushOrderToNavigationHistory", response.data.body);
      })
      .catch((e) => {
        error = e;
        commit("SET_ORDER", {});
        commit("SET_ORDER_LOADED", false);
      });
    // if 500 error means the order can't find a SKU, try to get the deprecated order
    if (error?.response?.status === 500) {
      response = await OrdersApi.get({ SourceOrderNumber: OrderNumber })
        .then((response) => {
          if (response?.data?.body?.data[0]) {
            commit("SET_DEPRECATED_ORDER", response?.data?.body?.data[0]);
            commit("SET_DEPRECATED_ORDER_LOADED", true);
          } else {
            commit("SET_DEPRECATED_ORDER", {});
            commit("SET_DEPRECATED_ORDER_LOADED", false);
          }
        })
        .catch(() => {
          commit("SET_DEPRECATED_ORDER", {});
          commit("SET_DEPRECATED_ORDER_LOADED", false);
        });
    }
    commit("SET_ORDER_SEARCHING", false);
    return response;
  },

  fetchTracking({ commit, state }, OrderNumber) {
    if (state.orderLoaded) {
      return OrdersApi.getTracking(OrderNumber)
        .then((response) => {
          commit("SET_ORDER_TRACKING", response.data.body);
        })
        .catch((e) => {
          throw e;
        })
        .finally(() => {});
    }
  },

  fetchDiscounts({ commit, state }, OrderNumber) {
    if (state.orderLoaded) {
      return OrdersApi.getDiscounts(OrderNumber)
        .then((response) => {
          commit("SET_ORDER_DISCOUNTS", response.data.body);
        })
        .catch((e) => {
          throw e;
        })
        .finally(() => {});
    }
  },

  fetchTransactions({ commit, state }, OrderNumber) {
    if (state.orderLoaded) {
      return OrdersApi.getTransactions(OrderNumber)
        .then((response) => {
          commit("SET_ORDER_TRANSACTIONS", response.data.body?.data);
        })
        .catch((e) => {
          throw e;
        })
        .finally(() => {});
    }
  },

  async updateOrder({ commit }, { OrderNumber, data }) {
    return await OrdersApi.patchOrder(OrderNumber, data)
      .then((response) => {
        commit("SET_ORDER", response.data.body);
      })
      .catch((e) => {
        throw e;
      })
      .finally(() => {});
  },

  async holdOrder({ commit }, { OrderNumber, data }) {
    return await OrdersApi.holdOrder(OrderNumber, data)
      .then((response) => {
        // TODO: this is a hacky way to update the order notes section
        commit("SET_ORDER_LOADED", false);
        setTimeout(() => {
          commit("SET_ORDER", response.data.body);
          commit("SET_ORDER_LOADED", true);
        }, 1);
      })
      .catch((e) => {
        console.log(e);
        throw e;
      })
      .finally(() => {});
  },

  async releaseOrder({ commit }, { OrderNumber, data }) {
    return await OrdersApi.releaseOrder(OrderNumber, data)
      .then((response) => {
        // TODO: this is a hacky way to update the order notes section
        commit("SET_ORDER_LOADED", false);
        setTimeout(() => {
          commit("SET_ORDER", response.data.body);
          commit("SET_ORDER_LOADED", true);
        }, 1);
      })
      .catch((e) => {
        console.log(e);
        throw e;
      })
      .finally(() => {});
  },

  async cancelOrder({ commit }, { OrderNumber, data }) {
    return await OrdersApi.cancelOrder(OrderNumber, data)
      .then((response) => {
        // TODO: this is a hacky way to update the order notes section
        commit("SET_ORDER_LOADED", false);
        setTimeout(() => {
          commit("SET_ORDER", response.data.body);
          commit("SET_ORDER_LOADED", true);
        }, 1);
      })
      .catch((e) => {
        console.log(e);
        throw e;
      })
      .finally(() => {});
  },

  async adjustOrder({ commit }, { OrderNumber, data }) {
    return await OrdersApi.adjustOrderV2(OrderNumber, data)
      .then((response) => {
        // TODO: this is a hacky way to update the order notes section
        commit("SET_ORDER_LOADED", false);
        setTimeout(() => {
          commit("SET_ORDER", response.data.body);
          commit("SET_ORDER_LOADED", true);
        }, 1);
      })
      .catch((e) => {
        console.log(e);
        throw e;
      })
      .finally(() => {});
  },

  async editTotals({ dispatch }, { OrderNumber, data }) {
    if (data.adjustment === undefined)
      throw new Error("Missing adjustment type.");
    return await dispatch("adjustOrder", { OrderNumber, data });
  },

  refreshOrder({ commit }, OrderNumber) {
    commit("SET_ORDER_LOADED", false);
    commit("SET_ORDER_SEARCHING", true);
    // TODO: this is a hacky way to update the order notes section
    return OrdersApi.getOrder(OrderNumber).then((response) => {
      commit("SET_ORDER", response.data.body);
      commit("SET_ORDER_LOADED", false);
      setTimeout(() => {
        commit("SET_ORDER_LOADED", true);
      }, 1);
    });
  },

  setOrder({ commit }, data) {
    commit("SET_ORDER", data);
    commit("SET_ORDER_LOADED", true);
  },

  async editOrderProducts({ dispatch }, { OrderNumber, data }) {
    return await dispatch("editOrderProducts", { OrderNumber, data });
  },

  pushOrderToNavigationHistory({ commit, getters }, order) {
    let history = getters.getNavigationHistory
      .filter((item) => item.orderNumber != order.SourceOrderNumber)
      .slice(0, navigationHistoryLimit);

    history.unshift({
      orderNumber: order.SourceOrderNumber,
      customerName: order.customer.FullName,
      lastAccessedAt: new Date().getTime(),
    });

    commit("SET_NAVIGATION_HISTORY", history);
  },
};

// mutations
const mutations = {
  SET_ORDERS(state, orders) {
    state.orders = orders;
  },
  SET_ORDERS_LOADED(state, loaded) {
    state.ordersLoaded = loaded;
  },
  SET_ORDERS_SEARCHING(state, searching) {
    state.ordersSearching = searching;
  },
  SET_ORDER(state, order) {
    state.order = order;
    state.order.tracking = null;
  },
  SET_DEPRECATED_ORDER(state, order) {
    state.deprecatedOrder = order;
  },
  SET_ORDER_LOADED(state, loaded) {
    state.orderLoaded = loaded;
  },
  SET_DEPRECATED_ORDER_LOADED(state, loaded) {
    state.deprecatedOrderLoaded = loaded;
  },
  SET_ORDER_SEARCHING(state, searching) {
    state.orderSearching = searching;
  },
  SET_ORDER_TRACKING(state, tracking) {
    state.order.tracking = tracking;
  },
  SET_ORDER_DISCOUNTS(state, discounts) {
    state.order.discounts = discounts;
  },
  SET_ORDER_TRANSACTIONS(state, transactions) {
    state.order = {
      ...state.order,
      transactions,
    };
  },
  SET_NAVIGATION_HISTORY(state, history) {
    localStorage.setItem("navigation-history", JSON.stringify(history));
    state.navigationHistory = history;
  },
  SET_ORDER_NOTES(state, notes) {
    state.order.notes = notes;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
